'use client';

import posthog from 'posthog-js';
import { useEffect } from 'react';

import { ANALYSIS_COOKIES_PROPERTY_NAME } from '@cineamo/ui/src/constants/cookie-constants';

import { cookieStorage } from '@cineamo/ui/src/helpers/cookie-helper';

import useCookieConsent from '@cineamo/ui/src/hooks/useCookieConsent';

import { IS_ANALYTICS_ENABLED } from './analytics-environment';

/**
 * PostHog config that ties the SDK to the analysis consent (CIN-15445, § 25 TDDDG).
 *
 * `cookieless_mode: 'on_reject'` plus `opt_out_capturing_by_default` makes every visitor without a
 * granted consent cookieless (verified on posthog-js 1.396.6: events are sent, nothing is stored; an explicit
 * `opt_out_capturing()` would also work but writes an opt-out flag to localStorage): no cookie, no localStorage/sessionStorage, no stored distinct id, no
 * session recording. Identity is a server-side hash, which needs `cookieless_server_hash_mode`
 * enabled in the PostHog project settings; without it those events are dropped.
 *
 * `granted` is read straight from the cookie, because this runs in an effect on a returning
 * visitor's first load, before `useCookieConsent` (always false on first render) has caught up.
 */
export function posthogConsentConfig(): { cookieless_mode: 'on_reject'; opt_out_capturing_by_default: boolean } {
    return {
        cookieless_mode: 'on_reject',
        opt_out_capturing_by_default: cookieStorage.getItem(ANALYSIS_COOKIES_PROPERTY_NAME) !== true
    };
}

/**
 * Switches the initialized client between cookieless and normal mode when the banner decision
 * changes, without a reload: `opt_in_capturing` resets the cookieless identity and starts
 * persisting, `opt_out_capturing` falls back to cookieless.
 */
export function usePostHogConsentSync(): void {
    const granted = useCookieConsent(ANALYSIS_COOKIES_PROPERTY_NAME);

    useEffect(() => {
        if (!IS_ANALYTICS_ENABLED || !posthog.__loaded) return;
        // Skip the no-op: first render is always `false`, and for a visitor who never consented
        // opting out would write the opt-out flag to localStorage.
        if (granted === posthog.has_opted_in_capturing()) return;
        if (granted) {
            posthog.opt_in_capturing();
        } else {
            // Back to the never-consented state: cookieless, and the stored opt-out flag removed again.
            posthog.set_config({ opt_out_capturing_by_default: true });
            posthog.opt_out_capturing();
            posthog.clear_opt_in_out_capturing();
        }
    }, [granted]);
}
